import { List, ListItem, ListItemText, ListItemIcon } from '@material-ui/core'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import React, { useState, useEffect, useCallback } from 'react'

import { IFundFields } from '@aletheia/eafunds/contentful'

import { getMarkdownFields } from './utils'

type TTOCField = {
  id: string
  name: string
}

function getTOCFields(Fund: IFundFields): TTOCField[] {
  return [
    { id: 'basic-info', name: 'Basic Info' },
    { id: 'highlighted-grants', name: 'Highlighted Grants' },
    ...getMarkdownFields(Fund),
    { id: 'payout-stats', name: 'Payouts Over Time' },
    { id: 'payout-reports', name: 'Payout Reports' },
    { id: 'funding-sources', name: 'Funding Sources' },
    { id: 'faq', name: `${Fund.shortTitle || Fund.title} Fund FAQ` },
    { id: 'fund-managers', name: 'Fund Managers' },
  ].filter((a) => {
    if (a.id === 'faq') return !!Fund.faqEntries && !!Fund.faqEntries.length
    return true
  })
}

type TElementVisibilityMap = Record<
  string,
  IntersectionObserverEntry['intersectionRatio']
>

type TOCProps = {
  Fund: IFundFields
  sidebar: boolean
}

const TOC: React.FC<TOCProps> = ({ Fund, sidebar }) => {
  const [elementVisibility, setElementVisibility] =
    useState<TElementVisibilityMap>({})
  const [observer, setObserver] = useState<IntersectionObserver | undefined>()

  const createObserver = useCallback(() => {
    // create observer
    const observerOpts = {
      threshold: [0.2, 0.4, 0.6, 0.8, 1],
      rootMargin: '60px 0px 0px 0px',
    }
    // use the IntersectionObserver primitive to get the percentage of an element that's on-screen
    // see https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver for details
    const observer = new IntersectionObserver((entries) => {
      const visibility = {} as TElementVisibilityMap
      for (const entry of entries) {
        // keep our current element up to date
        visibility[entry.target.id] = entry.intersectionRatio
      }
      setElementVisibility((prevVisibility) => ({
        ...prevVisibility,
        ...visibility,
      }))
    }, observerOpts)

    for (const field of getTOCFields(Fund)) {
      const el = document.getElementById(field.id)
      if (el) observer.observe(el)
    }

    setObserver(observer)
  }, [Fund])

  const disconnectObserver = useCallback(() => {
    if (observer) {
      observer.disconnect()
      setObserver(undefined)
    }
  }, [observer])

  const getHighlightElement = () => {
    if (!observer) return
    const highlightElements = Object.keys(elementVisibility).sort(
      (a, b) => elementVisibility[b] - elementVisibility[a],
    )
    return highlightElements[0]
  }

  useEffect(() => {
    if (sidebar) {
      // If we don't have an observer, create one
      if (!observer) createObserver()
      // cleanup function
      return () => {
        // if we do have an observer, disconnect it
        disconnectObserver()
      }
    }
  }, [sidebar, observer, createObserver, disconnectObserver])

  const tocEntries = getTOCFields(Fund)
  const highlightElement = getHighlightElement()
  return (
    <List>
      {tocEntries.map(({ id, name }) => (
        <ListItem
          key={id}
          button
          component="a"
          href={`#${id}`}
          selected={!!highlightElement && id === highlightElement}
        >
          {!sidebar && (
            <ListItemIcon>
              <ArrowForwardIcon />
            </ListItemIcon>
          )}
          <ListItemText>{name}</ListItemText>
        </ListItem>
      ))}
    </List>
  )
}

export default TOC
